import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { axisProps, fmtMoney, fmtMoneyShort, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Each doctor's share of the work and of the fees: visit volume, completion, and OPD revenue.
// The fees go only to readers with the Finance reports permission; the server leaves them out for
// anyone else, and this screen drops the matching columns and chart.

const MONEY_KEYS = new Set(['grossFees', 'refunds', 'net', 'avgFee']);

const COLUMNS = [
  { key: 'doctor', label: 'Doctor' },
  { key: 'visits', label: 'Visits', align: 'right' },
  { key: 'completed', label: 'Completed', align: 'right' },
  { key: 'cancelled', label: 'Cancelled', align: 'right' },
  { key: 'uniquePatients', label: 'Unique Patients', align: 'right' },
  { key: 'emergencies', label: 'Emergencies', align: 'right' },
  { key: 'grossFees', label: 'Fees', align: 'right', render: (r) => fmtMoney(r.grossFees) },
  { key: 'refunds', label: 'Refunds', align: 'right', render: (r) => (Number(r.refunds) > 0 ? <span className="text-rose-600">{fmtMoney(r.refunds)}</span> : '—'), csv: (r) => r.refunds },
  { key: 'net', label: 'Net', align: 'right', render: (r) => <span className="font-semibold">{fmtMoney(r.net)}</span>, csv: (r) => r.net },
  {
    key: 'avgFee', label: 'Avg Fee', align: 'right',
    render: (r) => (Number(r.visits) > 0 ? fmtMoney(Math.round(Number(r.net) / Number(r.visits))) : '—'),
    csv: (r) => (Number(r.visits) > 0 ? Math.round(Number(r.net) / Number(r.visits)) : ''),
    sortValue: (r) => (Number(r.visits) > 0 ? Number(r.net) / Number(r.visits) : 0),
  },
];

const DoctorLoadReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');

  const fetcher = useCallback(
    () => reportsAPI.getDoctorLoad({ from: range.from, to: range.to }),
    [range]
  );
  const { data, loading } = useReport(fetcher);

  // Absent until the report arrives; treated as allowed meanwhile so the layout does not jump for
  // the readers who do see the money.
  const money = data?.access?.money !== false;
  const columns = useMemo(() => (money ? COLUMNS : COLUMNS.filter((c) => !MONEY_KEYS.has(c.key))), [money]);

  const chartRows = useMemo(
    () => (data?.rows || []).map((r) => ({ doctor: r.doctor, visits: Number(r.visits), net: Number(r.net) })),
    [data]
  );

  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[300px]">
          <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />
        </div>
        <div className="pt-4"><PrintButton /></div>
      </div>

      <div className={`grid grid-cols-1 gap-5 ${money ? 'lg:grid-cols-2' : ''}`}>
        <ChartPanel title="Visits per doctor">
          {loading || chartRows.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-16">
              {loading ? 'Loading' : 'No visits in the selected range'}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, chartRows.length * 40)}>
              <BarChart data={chartRows} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
                <XAxis type="number" {...axisProps} allowDecimals={false} />
                <YAxis type="category" dataKey="doctor" {...axisProps} width={140} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="visits" radius={[0, 4, 4, 0]} barSize={18}>
              {chartRows.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
        {money && (
        <ChartPanel title="OPD revenue per doctor (net)">
          {loading || chartRows.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-16">
              {loading ? 'Loading' : 'No visits in the selected range'}
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, chartRows.length * 40)}>
              <BarChart data={chartRows} layout="vertical" margin={{ left: 8, right: 16 }}>
                <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
                <XAxis type="number" {...axisProps} tickFormatter={fmtMoneyShort} />
                <YAxis type="category" dataKey="doctor" {...axisProps} width={140} />
                <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
                <Bar dataKey="net" radius={[0, 4, 4, 0]} barSize={18}>
              {chartRows.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartPanel>
        )}
      </div>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'doctor', value: r.doctor, from: range.from, to: range.to })}
        title="Doctor performance"
        columns={columns}
        rows={data?.rows || []}
        csvName={`doctor-load-${range.from}-to-${range.to}`}
        emptyText="No visits in the selected range"
        footnote={money ? undefined : 'Fees, refunds and net revenue are shown to users with the Finance reports permission.'}
      />

      <ReportPrintLayout
        title="Doctor Load"
        subtitle={`Period: ${range.from} to ${range.to}`}
        tables={[{ title: 'Doctor performance', columns, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default DoctorLoadReport;
